import { $post } from '@/api'
import { Group, Unit } from '@/model/types/goods'
import $msg from '@/utils/message'
import { action, observable } from 'mobx'
import { ISupplier } from '@/model/types/supllier'

export interface IGoodsCategory {
    id: number
    name: string
    img_url: string
    group_name: string
    secondary_group_name: string
}

export interface IGenerateCode {
    new_code: string
    new_price_code: string[]
}

export interface IGoodProfile {
    negative_stock_mode: number
    stock_deduction_mode: number
}

// 用于存放编辑商品需要的公共信息
export class GoodsModel {
    // goodsClass
    @observable
    public goodsCategory: IGoodsCategory[] = []
    @observable
    public goodsGroup: Group[] = []
    public supplier: ISupplier[] = []
    public goodsUnit: Unit[] = []
    public goodsCustomUnit: Unit[] = [] // 新单位
    public goodsDefaultUnit: Unit[] = [] // 系统内置单位
    public autoScaleNum: string = '' // 自动生成的条码秤序号

    @observable
    public defaultSupplier: ISupplier | null = null

    @observable
    public generateCode: IGenerateCode | null = null

    @observable
    public goodProfile: IGoodProfile | null = null

    public init(generateCode: boolean = false, profile: boolean = false) {
        return Promise.all([
            this.initGoodsClassInfo(),
            this.initGoodsGroupInfo(),
            this.initGoodsUnit(),
            this.initSupplierList(),
            this.getGenerateCode(generateCode),
            this.initProfile(profile),
            this.getScaleCode(),
        ])
    }

    public initProfile(init: boolean = false) {
        return (
            init &&
            $post({
                url: '/goods/getshopgoodsprofile',
                params: {},
            }).then(data => {
                this.setGoodsProfile(data.profile)
            })
        )
    }

    public setGoodsProfile(profile: IGoodProfile) {
        this.goodProfile = profile
    }

    public initGoodsClassInfo() {
        return $post({
            url: '/goods/getgoodscategory',
            params: {},
        }).then(data => {
            this.setGoodsCategory(data.all_goods_category)
        })
    }

    public initGoodsGroupInfo() {
        return $post({
            url: '/goods/getgoodsgroup',
        }).then(res => {
            this.setGoodsGroup(res)
        })
    }

    public initGoodsUnit() {
        return Promise.all([this.getGoodsUnit(), this.getGoodsCustomUnit()]).then(res => {
            this.setGoodsDefaultUnit(res[0])
            this.setGoodsCustomUnit(res[1])
            const result = res[0].concat(res[1])
            this.setGoodsUnit(result)

            return result
        })
    }

    public getGenerateCode(generateCode: boolean = false) {
        return (
            generateCode &&
            $post({
                url: '/goods/generategoodscode',
            })
                .then(res => {
                    this.generateCode = res
                })
                .catch(e => {
                    $msg('error', e.message)
                })
        )
    }

    public getGoodsUnit() {
        return $post({
            url: '/goods/getunit',
        })
            .then(res => res)
            .catch(e => {
                $msg('error', e.message)
            })
    }

    public getGoodsCustomUnit() {
        return $post({
            url: '/goods/getcustomunit',
        })
            .then(res => res)
            .catch(e => {
                $msg('error', e.message)
            })
    }

    public initSupplierList() {
        return $post({
            url: '/warehouse/getshopsuppliers',
            params: {
                page: 1,
                limit: 100,
            },
        })
            .then(res => {
                this.setSupplierList(res.suppliers)
                this.defaultSupplier = res.suppliers.filter((supplier: ISupplier) => supplier.is_default)[0]
            })
            .catch(e => {
                $msg('error', e.message)
            })
    }

    public getScaleCode() {
        return $post({
            url: '/goods/gengoodsscalenum',
            params: {},
        }).then(res => {
            this.autoScaleNum = res.scale_num || ''
        })
    }

    @action
    public setGoodsCategory(list: IGoodsCategory[]) {
        this.goodsCategory = list
    }

    @action
    public setGoodsGroup(list: Group[]) {
        this.goodsGroup = list
    }

    @action
    public setGoodsUnit(list: Unit[]) {
        this.goodsUnit = list
    }

    @action
    public setGoodsCustomUnit(list: Unit[]) {
        this.goodsCustomUnit = list
    }

    @action
    public setGoodsDefaultUnit(list: Unit[]) {
        this.goodsDefaultUnit = list
    }

    @action setSupplierList(list: ISupplier[]) {
        this.supplier = list
    }
}

export const goodsModel = new GoodsModel()
